import { api } from "@/frontend/one-id-widget/api";
import config from "@/frontend/one-id-widget/config";
import createOneIdWidgetScript from "@/frontend/lib/createOneIdWidgetScript";
import OidcState from "@/frontend/lib/oidcState";
import { AgeVerification } from "@/frontend/lib/oneId/ageVerification";
import VerificationStorage from "@/frontend/lib/oneId/storage";
import { storeFrontApi } from "@/frontend/one-id-widget/storeFrontApi";
import { helpers } from "@/helpers";
import { last } from "lodash";
import { InstallMethodType, WidgetMessageType, WidgetRenderType, WidgetShop, WidgetVerification } from "@/types/frontend";
import {v4 as uuidv4} from "uuid";
import axios from "axios";

export class OneIdWidget {
    url: URL | null = null;
    appUrl: string = "";
    appApiBaseUrl: string = "";
    oneidClientId: string = "";
    shopDomain: string = "";
    ageRequired: number = 0;
    cartToken: string = "";
    customToken: string = 'one-id-' + uuidv4();
    isCartPage: boolean = false;
    isCheckoutPage: boolean = false;
    oneidDivElement: HTMLElement | null = null;
    containerElement: HTMLElement | null = null;
    widgetContainer: HTMLElement | null = null;
    actionButton: HTMLButtonElement | null = null;
    shippingAddressDiv: HTMLDivElement | null = null;
    contactInformationDiv: HTMLDivElement | null = null;
    installMethod: InstallMethodType = "Cart Snippet";
    byPass: boolean = false;
    retry: boolean = false;
    age_check_enabled: boolean = true;
    checkout_autofill_enabled: boolean = false;
    valid_age_limits: number[] = [18, 21, 25];
    shop: WidgetShop | null = null;
    toggleCheckout: boolean = false;
    actionForm: HTMLFormElement | null = null;
    // add all possible checkout button querySelector here
    checkoutButtonQuerySelectors: string[] = [
        "input[id='checkout']",
        "input[name='checkout']",
        "button[id='checkout']",
        "button[name='checkout']",
        "form[action='/cart'] input[type='submit']",
    ];

    cartFormQuerySelector: string = "form[action='/cart']";
    additionalCheckoutButtonsCollection: HTMLCollectionOf<HTMLElement> | [] = [];
    private oidcState: OidcState = new OidcState();

    constructor(containerElement: HTMLElement, ageRequired: number, installMethod: InstallMethodType, toggleCheckout: boolean = false) {
        // no support on IE
        this.containerElement = containerElement;
        this.ageRequired = ageRequired;
        this.installMethod = installMethod;
        this.appUrl = config.baseUrl!;
        this.appApiBaseUrl = config.api.url!;
        this.shopDomain = config.shopDomain;
        this.toggleCheckout = toggleCheckout;
    }

    public async initialize(): Promise<void> {
        await this.resolveCartToken();
        await this.resolveShop();

        if (!this.shop || this.shop!.has_plan_subscription_usage_exceeded || this.shop!.has_shop_suspended) {
            console.log("OneID Cart snippet / JS API / App Block is inactive / disabled / exceeded limit!");
            return;
        }

        this.loadCss();

        const proceed = await this.checkCurrentPage();

        if (!proceed) {
            return;
        }

        if (this.hasQuickCheckoutMode()) {
            console.log("quick checkout widget mode");
            this.enableActionButton();
            this.resolveQuickCheckout();
            return;
        }

        if (this.hasNormalMode()) {
            console.log("oneid initialized - ageVerify: " + this.ageRequired);
            this.addToCart(this.customToken).then(r => this.resolveAgeCheck()) ;
        } else {
            this.enableActionButton();
        }

    }
    private async addToCart (customToken: string): Promise<any> {
        await axios.post('/cart/update.js', {
            attributes: {
                'OneID Token': customToken
            }
        }).then(data => console.log(data));
    };
    private async checkCurrentPage(): Promise<boolean> {
        if (window.location.href.includes("/checkouts") && window.Shopify!.Checkout!.step === "contact_information") {
            this.isCheckoutPage = true;
            this.actionButton = document.querySelector("#continue_button");
            this.shippingAddressDiv = document.querySelector("div.section.section--shipping-address");
            this.contactInformationDiv = document.querySelector("div.section.section--contact-information");

            return true;
        }

        this.isCartPage = true;
        for (const i of this.checkoutButtonQuerySelectors) {
            if (document.querySelector(i)) {
                this.actionButton = document.querySelector(i) as HTMLButtonElement | null;
                break;
            }
        }

        this.additionalCheckoutButtonsCollection = document.getElementsByClassName(
            "additional-checkout-buttons"
        ) as HTMLCollectionOf<HTMLElement>;

        return true;
    }

    private async resolveCartToken(): Promise<void> {
        if (this.isCheckoutPage) {
            const checkoutUrl = new URL(window.location.href);
            this.cartToken = checkoutUrl.pathname.split("/")[3];
        } else {
            const cartResponse = await storeFrontApi.getCart();
            this.cartToken = cartResponse.token;
        }
    }

    private async resolveShop(): Promise<void> {
        this.shop = await api.getShop({
            install_method: this.installMethod,
        });
    }

    private hasQuickCheckoutMode(): boolean {
        if (!this.shop!.age_check_enabled && this.shop!.checkout_autofill_enabled) {
            return false;
        }

        if (!this.shop || !this.shop.has_widget_available) {
            return false;
        }

        return this.shop!.checkout_autofill_enabled && (!this.shop!.age_check_enabled || !this.ageRequired);
    }

    private hasNormalMode(): boolean {
        if (!this.shop || !this.shop.has_widget_available) {
            return false;
        }

        return this.ageRequired > 0 && this.shop!.age_check_enabled;
    }

    private loadCss(): void {
        const head = document.getElementsByTagName("head")[0];
        head.insertAdjacentHTML(
            "afterbegin",
            "<link rel='stylesheet' href='https://fonts.googleapis.com/css?family=Quicksand:400,500&display=swap' />"
        );
        head.insertAdjacentHTML("beforeend", `<link rel="stylesheet" href="${this.appUrl}/resources/oneid.css" />`);
    }

    private generateMessage(message: string, type: WidgetMessageType = "info", closable: boolean = true): void {
        if (!this.containerElement) {
            this.destroy();
            throw new Error("Can't generate alert banner when container doesn't exist.");
        }

        const leading = document.createElement("div");
        leading.className = "oid-leading";
        leading.innerHTML = "&nbsp;";

        const main = document.createElement("div");
        main.className = "oid-main";
        main.innerHTML = message;

        const container = document.createElement("div");
        container.className = "oid-alert oid-" + type;
        container.appendChild(leading);
        container.appendChild(main);

        if (closable) {
            const close = document.createElement("div");
            close.className = "oid-close";
            close.setAttribute("onclick", "this.parentElement.style.display='none';");
            close.innerHTML = "&nbsp;";
            container.appendChild(close);
        }

        this.containerElement.prepend(container);
    }

    private clearLocalStorage() {
        VerificationStorage.clear();
    }

    private resolveQuickCheckout() {
        const flag: string | null = new URLSearchParams(window.location.search).get("flag");

        if (!flag || flag !== "success") {
            this.renderWidget("quick-checkout");
        }

        if (this.oidcState.hasError()) {
            this.generateMessage(this.oidcState.message() || "An unknown error occurred. Please try again.", "error");
        }
    }

    private async resolveAgeCheck(): Promise<void> {
        const ageVerification = new AgeVerification(this.shop!, this.cartToken,this.customToken, this.ageRequired);
        await ageVerification.initialise();

        if (ageVerification.invalid()) {
            this.enableActionButton();
            this.generateMessage("Please try OneID again or continue to checkout.", "error");
            return;
        }

        if (ageVerification.passed()) {
            this.generateMessage(
                "<strong>Success!</strong> You have successfully verified your age is over " +
                    this.ageRequired +
                    " using <strong>OneID</strong>",
                "success"
            );

            this.enableActionButton();
        } else if (ageVerification.failed()) {
            this.generateMessage(
                "You have failed the <strong>OneID Age Check</strong>. To proceed with your purchase " +
                    "<strong>please remove any age restricted items</strong>.",
                "error"
            );
            this.generateRetryButton();

            this.disableActionButton();

            if (this.isCheckoutPage && this.shippingAddressDiv) {
                this.shippingAddressDiv.style.display = "none";
                this.contactInformationDiv!.style.display = "none";
            }
        } else if (ageVerification.required()) {
            if (this.oidcState.hasError()) {
                this.generateMessage(this.oidcState.message() || "Please try OneID again or continue to checkout.", "error");
            }
            this.disableActionButton();
            this.renderWidget();
        }

        const verification = VerificationStorage.get();
        if (verification) {
            this.resolveAgeCheckCheckoutAutofill(verification);
        }
    }

    private async resolveAgeCheckCheckoutAutofill(verification: WidgetVerification): Promise<void> {
        if (!verification.address_key || !this.actionButton) {
            return;
        }
        const response = await api.getVerificationUserAddress(verification);
        if (!response) {
            return;
        }

        // mutate checkout form action
        const actionForm: HTMLFormElement | undefined = last(document.querySelectorAll(this.cartFormQuerySelector));

        if (!actionForm) {
            console.log("CART form action not found.");
            return;
        }

        if (this.shop!.install_method == "Checkout Snippet") {
            const country = document.getElementById("checkout_shipping_address_country")! as HTMLInputElement;
            const address1 = document.getElementById("checkout_shipping_address_address1")! as HTMLInputElement;
            const city = document.getElementById("checkout_shipping_address_city")! as HTMLInputElement;
            const zip = document.getElementById("checkout_shipping_address_zip")! as HTMLInputElement;
            const emailOrPhone = document.getElementById("checkout_email_or_phone")! as HTMLInputElement;
            const firstName = document.getElementById("checkout_shipping_address_first_name")! as HTMLInputElement;
            const lastName = document.getElementById("checkout_shipping_address_last_name")! as HTMLInputElement;

            if (country) {
                country.value = helpers.resolveCountry(response.country);
            }
            if (address1) {
                address1.value = response.street_address;
            }
            if (city) {
                city.value = response.locality;
            }
            if (zip) {
                zip.value = response.postal_code;
            }
            if (emailOrPhone) {
                emailOrPhone.value = response.email_or_phone;
            }
            if (firstName) {
                firstName.value = response.first_name;
            }
            if (lastName) {
                lastName.value = response.last_name;
            }
        } else {
            const connectingChar = actionForm!.action.includes("?") ? "&" : "?";
            setTimeout(function() {
                actionForm!.action =
                    actionForm!.action! +
                    connectingChar +
                    "step=contact_information" +
                    "&checkout[shipping_address][company]=" +
                    "&checkout[shipping_address][first_name]=" +
                    encodeURIComponent(response.first_name) +
                    "&checkout[shipping_address][last_name]=" +
                    encodeURIComponent(response.last_name) +
                    "&checkout[shipping_address][address1]=" +
                    encodeURIComponent(response.street_address) +
                    "&checkout[shipping_address][address2]=" +
                    "&checkout[shipping_address][city]=" +
                    encodeURIComponent(response.locality) +
                    "&checkout[shipping_address][country]=" +
                    encodeURIComponent(response.country) +
                    "&checkout[shipping_address][zip]=" +
                    encodeURIComponent(response.postal_code) +
                    "&checkout[shipping_address][province]=" +
                    encodeURIComponent(response.region) +
                    "&checkout[email]=" +
                    encodeURIComponent(response.email_or_phone);
            }, 1000);
        }

        this.removeUserAddress(verification);
    }

    private async removeUserAddress(verification: WidgetVerification): Promise<void> {
        try {
            await api.removeVerificationUserAddress({
                id: verification.id,
                cart_token: this.cartToken,
            });
        } catch (e) {
            console.log("unable to discard address.");
        }
    }

    private disableActionButton() {
        const disableButton = function(this: OneIdWidget) {
            if (!this.actionButton) {
                return;
            }
            this.actionButton.disabled = true;
            if (this.toggleCheckout) {
                this.actionButton.style.visibility = "hidden";
            }

            if (this.additionalCheckoutButtonsCollection.length) {
                for (const elm of this.additionalCheckoutButtonsCollection) {
                    elm.style.visibility = "hidden";
                }
            }
        }.bind(this);

        disableButton();

        let attempts = 0;
        const interval = setInterval(function(this: OneIdWidget) {
            disableButton();
            attempts++;
            if (attempts === 3) {
                clearInterval(interval);
            }
        }, 300);
    }

    private enableActionButton() {
        if (!this.actionButton) {
            return;
        }
        this.actionButton.disabled = false;
        if (this.toggleCheckout) {
            this.actionButton.style.visibility = "visible";
        }
        if (this.additionalCheckoutButtonsCollection.length) {
            for (const elm of this.additionalCheckoutButtonsCollection) {
                elm.style.visibility = "visible";
            }
        }
    }

    private renderWidget(mode: WidgetRenderType = "age-check"): void {
        if (mode == "quick-checkout") {
            if (!this.shop!.age_check_enabled && this.shop!.checkout_autofill_enabled) {
                return;
            }
            this.generateQuickCheckoutButton();
            return;
        }
        // console.log('this.actionButton: ', this.actionButton);
        this.disableActionButton();

        this.generateOneIdButton();
        this.generateMessage(
            "This cart contains <strong>age restricted product(s)</strong>. You must be aged " +
                this.ageRequired +
                " years or older to be able to complete this purchase.",
            "info",
            false
        );

        if (!["TU In-checkout", "TU Pre-checkout"].some((f) => this.installMethod.includes(f))) {
            this.generateByPassButton();
        }

        if (this.isCheckoutPage && this.shippingAddressDiv) {
            this.shippingAddressDiv.style.display = "none";
            this.contactInformationDiv!.style.display = "none";
        }
    }

    private resolveQuickCheckoutState() {
        const hostName = "https://" + window.location.hostname;
        let returnUrl = hostName + "/cart";
        if (this.shop!.install_method == "Checkout Snippet") {
            returnUrl = hostName + "/checkout";
        }

        return {
            cartToken: this.cartToken,
            customToken: this.customToken,
            returnUrl: returnUrl,
            shop: this.shopDomain,
            shopAddress: {
                shop_domain: this.shop!.shop_domain,
                address1: this.shop!.address1,
                address2: this.shop!.address2,
                city: this.shop!.city,
                province: this.shop!.province,
                province_code: this.shop!.province_code,
                country: this.shop!.country,
                country_code: this.shop!.country_code,
                zip: this.shop!.zip,
                phone: this.shop!.phone,
                timezone: this.shop!.iana_timezone,
            },
            requestType: "quick-checkout",
            quickCheckoutId: helpers.getUniqueKey(), // uuid()
        };
    }

    private generateQuickCheckoutButton(): void {
        if (!this.containerElement) {
            this.destroy();
            console.log("Can't generate button when container doesn't exist.");
            throw new Error("Can't generate button when container doesn't exist.");
        }

        let script = document.createElement("script");
        const node = document.createTextNode(`window.OneIDBaseURL = "${config.oneIdWidgetEndpoint}";`);
        script.appendChild(node);

        const modalContainer = document.createElement("div");
        modalContainer.id = "oneid-modal";
        modalContainer.className = "oneid-modal";
        modalContainer.insertAdjacentHTML(
            "afterbegin",
            "<div class=\"oneid-modal-content\"><div class=\"oneid-modal-header\">" +
                "<span>&nbsp;</span><span id=\"oneid-modal-close\" class=\"oneid-modal-close\">&times;</span>" +
                "</div><div id=\"oneid-modal-body\" class=\"oneid-modal-body\"></div></div>"
        );

        this.containerElement.appendChild(modalContainer);
        this.widgetContainer = document.createElement("div");
        this.widgetContainer.id = "oneid-widget-container";
        this.widgetContainer.appendChild(script);

        script = createOneIdWidgetScript(this.shop!.oneid_client_id, "email address phone", this.resolveQuickCheckoutState());
        this.widgetContainer.appendChild(script);
        this.widgetContainer.prepend(script);

        const modalBodyContainer = document.getElementById("oneid-modal-body")!;
        modalBodyContainer.appendChild(this.widgetContainer);
        this.containerElement.appendChild(modalContainer);

        modalContainer.style.display = "none";
        this.clearLocalStorage();
        this.initializeCheckoutWithOneIdButton();
    }

    private initializeCheckoutWithOneIdButton() {
        if (!this.containerElement || !this.shop || !this.actionButton || !this.widgetContainer) {
            this.destroy();
            return;
        }

        const actionButtonParentNode = this.actionButton.parentNode! as HTMLDivElement;

        actionButtonParentNode.insertAdjacentHTML(
            "beforeend",
            "<div id=\"oneid-quick-checkout-button\"><a>" +
                "<img src=\"https://public-assets.myoneid.co.uk/latest/images/icon-solid-white.svg\" class=\"oneid-icon\" />" +
                "<span>Checkout with <strong>OneID</strong></span></a></div>"
        );

        const oneIdQuickCheckoutButton = document.getElementById("oneid-quick-checkout-button")!;
        const oneIdModal = document.getElementById("oneid-modal")!;
        const oneIdModalClose = document.getElementById("oneid-modal-close")!;
        oneIdQuickCheckoutButton.addEventListener("click", (e) => {
            oneIdModal.style.display = oneIdModal.style.display == "none" ? "block" : "none";
        });
        oneIdModalClose.addEventListener("click", (e) => {
            oneIdModal.style.display = "none";
        });
    }

    private generateOneIdButton(): void {
        if (!this.containerElement) {
            this.destroy();
            console.log("Can't generate button when container doesn't exist.");
            throw new Error("Can't generate button when container doesn't exist.");
        }

        let scriptTag = document.createElement("script");
        const node = document.createTextNode(`window.OneIDBaseURL = "${config.oneIdWidgetEndpoint}";`);
        scriptTag.appendChild(node);
        this.containerElement.appendChild(scriptTag);

        const scope = "age_over_" + this.ageRequired;
        const scopes = this.shop!.checkout_autofill_enabled ? scope + " email address phone" : scope;

        // oneid button
        scriptTag = createOneIdWidgetScript(this.shop!.oneid_client_id, scopes, {
            cartToken: this.cartToken,
            customToken: this.customToken,
            returnUrl: window.location.href,
            ageOver: this.ageRequired,
            shop: this.shop!.shop_domain,
            requestType: "age-check",
            checkoutAutoFill: this.shop!.checkout_autofill_enabled,
        });

        this.containerElement.prepend(scriptTag);
    }

    private generateByPassButton(): void {
        if (!this.containerElement || !this.actionButton) {
            this.destroy();
            throw new Error("Can't generate button when container or action button doesn't exist.");
        }

        if (!this.shop) {
            this.destroy();
            throw new Error("Unable to read shop, can't generate button.");
        }

        if (this.shop!.widget_bypass) {
            this.containerElement.insertAdjacentHTML(
                "beforeend",
                "<div class=\"oid-bypass oid-section\">" +
                    "<a id=\"oid-skip\" class=\"oid-button oid-plain-button\">" +
                    "<span><strong>Skip OneID Age Verification</strong></span></a>" +
                    "<div class=\"oid-help\"><span>You will need to provide proof of age by other means</span></div>" +
                    "<div id=\"oid-wrapper\">" +
                    "<div class=\"oid-text\">" +
                    "Your order may be subject to delay or cancellation as " +
                    "we may need you to provide proof of age manually." +
                    "</div>" +
                    "<div class=\"oid-row\">" +
                    "<div class=\"oid-column\">" +
                    "<a id=\"oid-skip-cancel\" class=\"oid-button secondary\">" +
                    "<span><strong>Cancel</strong></span>" +
                    "</a>" +
                    "</div>" +
                    "<div class=\"oid-column\">" +
                    "<a id=\"oid-skip-now\" class=\"oid-button\">" +
                    "<span><strong>Yes, skip verification</strong></span>" +
                    "</a>" +
                    "</div>" +
                    "</div>" +
                    "</div>" +
                    "</div>"
            );

            const oidWrapper = document.getElementById("oid-wrapper")!;
            const oidSkip = document.getElementById("oid-skip")!;
            const oidSkipCancel = document.getElementById("oid-skip-cancel")!;
            const oidSkipNow = document.getElementById("oid-skip-now")!;

            oidSkip.addEventListener("click", (event) => {
                if (oidWrapper && oidSkip) {
                    oidWrapper.style.display = "block";
                    oidSkip.style.display = "none";
                }
            });

            oidSkipCancel.addEventListener("click", (event) => {
                if (oidWrapper && oidSkip) {
                    oidWrapper.style.display = "none";
                    oidSkip.style.display = "block";
                }
                this.disableActionButton();
            });

            oidSkipNow.addEventListener("click", (event) => {
                this.enableActionButton();
            });
        }
    }

    private generateRetryButton(): void {
        if (!this.containerElement || !this.actionButton) {
            this.destroy();
            throw new Error("Can't generate button when container or action button doesn't exist.");
        }

        if (!this.shop) {
            this.destroy();
            throw new Error("Unable to read shop, can't generate button.");
        }

        if (this.shop!.widget_retry) {
            this.containerElement.insertAdjacentHTML(
                "beforeend",
                "<div class=\"oid-row oid-section\">" +
                    "<div class=\"oid-column\">" +
                    "&nbsp;" +
                    "</div>" +
                    "<div class=\"oid-column\">" +
                    "<a  id=\"oid-retry\" class=\"oid-button primary\">" +
                    "<span><strong>Retry</strong></span>" +
                    "</a>" +
                    "</div>" +
                    "</div>"
            );

            const oidRetry = document.getElementById("oid-retry")!;

            oidRetry.addEventListener("click", (event) => {
                this.clearLocalStorage();
                window.location.href = window.location.href.split("?")[0] + "?state=" + btoa("{\"flag\":\"retry\"}");
            });
        }
    }

    public destroy() {
        if (this.actionButton && this.containerElement) {
            this.disableActionButton();
            if (this.isCheckoutPage && this.shippingAddressDiv) {
                this.shippingAddressDiv.style.display = "block";
                this.contactInformationDiv!.style.display = "block";
            }
            this.containerElement.style.display = "none";
        }
    }
}
